import moment from 'moment';
import { toast } from 'react-toastify';
import { getAsset } from '@/utils/index';
import axios from './axios';
import { DEFAULT_TAG_COLOR } from './constants';

export function getVersion(date: any) {
  if (!date) date = new Date();
  return `?v=${moment(date).format('HHmmss')}`;
}

export function setWhiteLabel(tour) {
  if (!tour || !tour.user) return;
  const { client } = tour;
  const u = tour.user;
  // eslint-disable-next-line no-nested-ternary
  const user = u.admin?.id === 1 ? u : u.admin && !(u.domainUrl && u.whitelabelId) ? u.admin : u;
  const whitelabelActive = user.whitelabelId;

  const clientLogoActive = client && client.whitelabelId && client.whitelabelLogo;
  const w = client && client.whitelabelId && clientLogoActive ? client : user;
  const c = client && client.whitelabelId ? client : user;

  const website = whitelabelActive && w.website ? w.website : 'https://treedis.com';
  const logoUrl = `${process.env.NEXT_PUBLIC_CDN}/${clientLogoActive ? 'clients' : 'users'}/${
    clientLogoActive ? client.id : user.id
  }/logo.png${getVersion(clientLogoActive ? client.updatedAt : user.updatedAt)}`;

  const whiteLabelData = {
    primaryColor: whitelabelActive ? c.primaryColor || '#292929' : '#292929',
    secondaryColor: whitelabelActive ? c.secondaryColor || '#2055ff' : '#2055ff',
    logo: whitelabelActive ? logoUrl : getAsset('logo-white.svg'),
    website: website.includes('https://') ? website : `https://${website.replace('http://', '')}`,
    domainUrl: whitelabelActive ? user.domainUrl || 'my.treedis.com' : 'my.treedis.com',
    active: whitelabelActive,
  };

  document.documentElement.style.setProperty('--color-primary', `${whiteLabelData.primaryColor}`);
  document.documentElement.style.setProperty(
    '--color-secondary',
    `${whiteLabelData.secondaryColor}`,
  );

  this.setState({ whiteLabelData });
}

export async function getICEServers() {
  const username = process.env.NEXT_PUBLIC_XIRSYS_USERNAME;
  const secret = process.env.NEXT_PUBLIC_XIRSYS_SECRET;
  const res = await axios.put(
    'https://global.xirsys.net/_turn/crm',
    {
      format: 'urls',
      expire: 40,
    },
    {
      headers: {
        Authorization: `Basic ${btoa(`${username}:${secret}`)}`,
      },
    },
  );
  const response = res.data;
  if (!response) throw new Error('ICE failed to respond, please retry');
  const servers = response.v;
  if (response.s === 'ok' && servers.iceServers) {
    return [servers.iceServers];
  }
  throw new Error(`ICE failed to respond, please retry: ${response.v}`);
}

export async function fetchData(_url = '', method = 'get', body: any = null, config: any = {}) {
  let url = `v1/${_url}`;
  if (_url.includes('https://')) url = _url;

  const response = await axios[method.toLowerCase()](url, body, config);

  if (response.status === 200) {
    return response.data;
  }
  if (response.data) toast.error(response.data.message);

  return null;
}

export function getPanoramaIndex(panoramas: any[], id: number) {
  return panoramas.findIndex((p: any) => p.id === id);
}

export function getQueryVariable(query: string, variable: string) {
  if (!query || !variable) return false;
  query = decodeURIComponent(query.split('?')[1]);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return false;
}

export function deleteQueryVariable(url: string, param: string): string {
  if (!url || !param || !url.includes(param)) return url;

  const urlInstance = new URL(url);
  const urlWithoutSearchParams = `${urlInstance.origin}${urlInstance.pathname}`;

  const searchParams = new URLSearchParams(urlInstance.search);
  searchParams.delete(param);
  if (!searchParams.size) return urlWithoutSearchParams;

  return `${urlWithoutSearchParams}?${decodeURIComponent(searchParams.toString())}`;
}

export function getAllQueryVariables(query: string, prefix = '&') {
  if (!query) return '';
  return `${prefix}${query.split('?').pop()}`;
}

export function getYoutubeVideoQueryVariable(query: string, variable: string) {
  if (!query || !variable) return '';
  if (getQueryVariable(query, variable)) {
    return getQueryVariable(query, variable);
  }
  return query.split('/').pop();
}

export function getVideoLink(
  url: string,
  settings?: {
    autoplay?: number | boolean;
    mute?: boolean;
    subtitles?: string;
    dnt?: boolean;
    fullscreen?: boolean;
  },
) {
  if (!url) return '';
  let autoplay;
  if (settings && typeof settings.autoplay === 'boolean') {
    autoplay = settings.autoplay ? 1 : 0;
  } else {
    autoplay = settings?.autoplay === undefined ? 1 : settings?.autoplay;
  }

  const mute = settings?.mute === undefined ? false : settings?.mute;
  let muteQueryParam: string = mute ? '&mute=1' : '';

  let urlC = [];
  if (url.includes('https://vimeo.com/')) {
    urlC = url.split('https://vimeo.com/');
  } else if (url.includes('https://player.vimeo.com/video/')) {
    urlC = url.split('https://player.vimeo.com/video/');
  }
  if (urlC.length > 1) {
    muteQueryParam = mute ? '&&muted=1' : '';
    const subtitlesParam = settings?.subtitles ? `&&texttrack=${settings?.subtitles}` : '';

    let link = `https://player.vimeo.com/video/${
      urlC[1]
    }?autoplay=${autoplay}&title=0&byline=0&portrait=0${muteQueryParam}${subtitlesParam}&dnt=${
      settings?.dnt ? 1 : 0
    }&fullscreen=${settings?.fullscreen ? 1 : 0}`;
    if (urlC[1].includes('/')) {
      const videoId = urlC[1].split('/')[0];
      const videoKey = urlC[1].split('/')[1].split('?')[0];
      link = `https://player.vimeo.com/video/${videoId}?h=${videoKey}&autoplay=${autoplay}&title=0&byline=0&portrait=0${muteQueryParam}${subtitlesParam}&dnt=${
        settings?.dnt ? 1 : 0
      }`;
    }
    return link;
  }
  const subtitlesParam = settings?.subtitles ? `&&hl=${settings?.subtitles}` : '';
  return `https://www.youtube.com/embed/${getYoutubeVideoQueryVariable(
    url,
    'v',
  )}?autoplay=${autoplay}${muteQueryParam}${subtitlesParam}&dnt=${settings?.dnt ? 1 : 0}`;
}

export function isVimeoUrl(url) {
  return /^(https?:)?\/\/((player|www)\.)?vimeo\.com(?=$|\/)/.test(url);
}

export function getBaseURL() {
  return window.location.href.split('?')[0];
}

export function buildLink(s: string, rotation: any, m: any = null, target: any = null) {
  let link = `${getBaseURL()}?s=${s}&x=${rotation.x}&y=${rotation.y}`;
  if (m) link += `&m=${m}`;
  if (target) link += `&target=${target}`;
  const searchParams = new URLSearchParams(window.location.search);
  const ticket = searchParams.get('ticket');
  if (ticket && ticket.length > 0) link += `&ticket=${ticket}`;

  // Make sure note query param is not deleted automatically (we will delete it manually)
  const note = searchParams.get('note');
  if (note && note.length > 0) link += `&note=${note}`;

  return link;
}

export function hexToRgb(hex: string = DEFAULT_TAG_COLOR.hex, decimal?: boolean) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const r = result ? parseInt(result[1], 16) : DEFAULT_TAG_COLOR.rgb.r;
  const g = result ? parseInt(result[2], 16) : DEFAULT_TAG_COLOR.rgb.g;
  const b = result ? parseInt(result[3], 16) : DEFAULT_TAG_COLOR.rgb.b;
  return decimal ? { r: r / 255, g: g / 255, b: b / 255 } : { r, g, b };
}

export function copyToClipboard(text: string) {
  const textarea = document.createElement('textarea');
  textarea.textContent = text;
  textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in Microsoft Edge.
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy'); // Security exception may be thrown by some browsers.
  } catch (ex) {
    console.warn('Copy to clipboard failed.', ex);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

export function updatePanoRotation(north: number, pos: any) {
  const { minimaps, selectedMinimap } = this.state;
  this.panoPose = pos;
  if (!minimaps || minimaps.length === 0 || selectedMinimap === null) return;
  const mapNorth = minimaps[selectedMinimap].north || 0;
  const y = pos.yaw > 0 ? 360 - pos.yaw : -1 * pos.yaw;
  const diff = north - y;
  const mapWalkerRotation = mapNorth + diff;
  this.setState({ mapWalkerRotation });
}

export const downloadFloorPlan = async (url: string, plan: any) => {
  const pdfName = plan?.name || '';
  const pdfType = plan?.type || 'pdf';
  const fileName = pdfName.toLowerCase().endsWith(`.${pdfType}`)
    ? pdfName
    : `${pdfName}.${pdfType}`;
  try {
    const res = await axios.post(
      'v1/public/downloadPdf',
      { url, pdfName },
      { responseType: 'blob' },
    );
    const blob = new Blob([res.data]);
    const objectUrl = (window as any).URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    await (window as any).URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error(err.message);
  }
};

export const validateEmail = (email: string): boolean => {
  const regex = /^([\w-.]+@([\w-]+\.)+[\w-]{2,})+?$/;
  return regex.test(email);
};

export const round = (value, decimals) =>
  Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);

export const openLinkInNewTab = (linkText: string) => {
  const link = document.createElement('a');
  link.href = linkText;
  link.target = '_blanc';
  document.body.appendChild(link);
  link.click();
  link.remove();
};
